import type {
  AuthUser,
  MemberPasswordUpdatePayload,
  MemberProfileUpdatePayload
} from '~/types/types'

export interface StaffNotificationItem {
  id: number
  type: string
  title: string
  body: string | null
  data?: { route?: string, [key: string]: unknown } | null
  read_at: string | null
  created_at: string
}

export interface StaffNotificationsPage {
  data: StaffNotificationItem[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

/**
 * Thin client around the /api/auth/* endpoints that cover the authenticated
 * user's own profile and password, plus /api/tenant/me/notifications (the
 * bell in AppShell.vue — see StaffNotificationController on the backend).
 * (The Zerotohero-era equivalent lived under /api/member/* with extras for
 * affiliate balances that agency-saas hasn't built yet; getPaymentDetail()
 * remains a stub for the QPay polling component.)
 */
export const useMemberRepository = () => {
  const api = useApi()

  const loadNotifications = async (params: { page?: number, per_page?: number } = {}): Promise<StaffNotificationsPage> => {
    return await api.get<StaffNotificationsPage>('/api/me/notifications', { query: params as Record<string, unknown> })
  }

  const unreadCounts = async (): Promise<{ unread_count: number, messages_unread_count: number }> => {
    const res = await api.get<{ data: { unread_count: number, messages_unread_count: number } }>('/api/me/unread-counts')
    return res.data
  }

  const markNotificationRead = async (id: number): Promise<void> => {
    await api.post(`/api/me/notifications/${id}/read`)
  }

  const markAllNotificationsRead = async (): Promise<void> => {
    await api.post('/api/me/notifications/read-all')
  }

  const updateProfile = async (payload: MemberProfileUpdatePayload): Promise<AuthUser> => {
    return await api.patch<AuthUser, MemberProfileUpdatePayload>('/api/auth/profile', {
      body: payload
    })
  }

  const updatePassword = async (payload: MemberPasswordUpdatePayload): Promise<{ message: string }> => {
    return await api.patch<{ message: string }, MemberPasswordUpdatePayload>('/api/auth/password', {
      body: payload
    })
  }

  const deleteAccount = async (): Promise<{ message: string }> => {
    return await api.del<{ message: string }>('/api/auth/account')
  }

  // QPay payment-status polling. Backend route doesn't exist yet — stubbed
  // for QPayView.vue. Replace with real endpoint when QPay flow is wired up.
  const getPaymentDetail = async (_id: number | string) => {
    if (import.meta.dev) {
      console.warn('[memberRepository] getPaymentDetail() not yet wired to a real endpoint.')
    }
    return null as null | { id: number; status: string }
  }

  return {
    updateProfile,
    updatePassword,
    deleteAccount,
    getPaymentDetail,
    loadNotifications,
    unreadCounts,
    markNotificationRead,
    markAllNotificationsRead
  }
}
